import {
  config,
  GENDER,
  REPRO_KIND,
  PREGNANCY_OUTCOME,
  SIBLING_TYPE,
  TWIN_TYPE
} from './config';
import { normalizePregnancyRecord } from './pregnancyNormalize';
import { SIBLING_TYPE_BY_COUNT, ZYGOSITY_TO_TWIN_TYPE } from './twinLinkage';

// Sibship type by how many fetuses one pregnancy draws — the layout's twin sub-group reads this.

// A pregnancy's recorded zygosity → the twin-line notation (Bennett). Anything unrecognised or
// absent falls through to `unknown`, which draws the "?" — the honest reading when zygosity has not
// been established.

// Track B (pregnancy-record model): the pedigree DERIVES its pregnancy + gamete-donor
// render nodes from the carrier's pregnancy records (Pregnancy collection, surfaced on each
// member as `pregnancies: [record]`). Derived nodes are NOT persons — they exist only in
// the render's normalized node array, so they never get a UPN, a person sidebar, or a place
// in member-based exports. This is the seam that replaces the B1a/B1b config-schema storage;
// the layout engine + Track A symbols/lines consume the output unchanged.
//
// A pregnancy record where `conceivedWith === 'partner'` (or 'partnerAndDonor') descends
// from the carrier+partner union; a donor-conceived one hangs off the carrier alone and the
// donors are satellites (donorOfPids). A live birth (or a record with `bornMemberId`) renders
// NO pregnancy node — it has become a real child; the record is kept only as history. A
// `hidden` record is kept (still listed in the Reproductive sidebar) but not drawn.
//
// PURE + no d3 → unit-testable. Inert (returns the input array) when the flag is off or no
// member carries a pregnancy record, so pedigrees without reproductive data are unchanged.

const DONOR_GENDER = {
  sperm: GENDER.male,
  ovum: GENDER.female,
  embryo: GENDER.unknown
};

// Does this FETUS draw a symbol on the pedigree? A born fetus has become a real person and is
// drawn as one; a live birth with no linked member yet draws nothing (the clinician has still to
// add the child); `hideInPedigree` is the per-fetus hide. The record-level `hidden` is checked
// separately, upstream, because it suppresses every fetus of the pregnancy.
export const fetusRendersAsNode = (fetus) =>
  !!fetus &&
  !fetus.hideInPedigree &&
  fetus.outcome !== PREGNANCY_OUTCOME.liveBirth &&
  fetus.bornMemberId == null;

const partnerConceived = (rec) =>
  rec.conceivedWith === 'partner' || rec.conceivedWith === 'partnerAndDonor';

export function deriveReproNodes(members) {
  if (!config.ENABLE_REPRO_NODES || !Array.isArray(members)) {
    return members || [];
  }
  const anyRecords = members.some(
    (m) => Array.isArray(m.pregnancies) && m.pregnancies.length > 0
  );
  if (!anyRecords) return members; // byte-identical when there are no pregnancy records

  // Shallow-copy every member so childrenIds can be extended without mutating the input.
  const result = members.map((m) => ({
    ...m,
    childrenIds: [...(m.childrenIds || [])]
  }));
  const findByMemberId = (id) =>
    id == null
      ? null
      : result.find(
          (m) =>
            String(m._id) === String(id) ||
            String(m.uid) === String(id) ||
            String(m.pid) === String(id)
        );

  let nextPid =
    Math.max(0, ...result.map((m) => (typeof m.pid === 'number' ? m.pid : 0))) +
    1;
  const derived = [];

  for (const carrier of result) {
    const pregs = Array.isArray(carrier.pregnancies) ? carrier.pregnancies : [];
    for (const rawRec of pregs) {
      // Normalise FHQ-vs-Bennett outcome + field-name differences on read (legacy FHQ
      // records store `delivered`/`completedWeeks`/`twins_triplets`); pedigree-authored
      // records pass through unchanged.
      const rec = normalizePregnancyRecord(rawRec);
      // A `hidden` record is kept as history (still listed in the Reproductive sidebar) but
      // nothing about it is drawn — no fetus nodes and no donors. Whether an individual fetus
      // draws is decided per fetus below (live birth / bornMemberId / hideInPedigree), because
      // one twin can be born or hidden while its sibling is not.
      if (rec.hidden) continue;

      // `gender` is the assigned sex at birth: a non-male carrier occupies the mother slot
      // (they gestate). The other genetic parent slot is the in-graph partner only when the
      // pregnancy was partner-conceived; a donor is never a parent (it is a satellite).
      const carrierIsMother = carrier.gender !== GENDER.male;
      const partner = partnerConceived(rec)
        ? findByMemberId(rec.otherParentMemberId)
        : null;

      const uid = String(rec._id ?? `preg-${carrier.pid}`);
      // ONE NODE PER FETUS (Desktop parity). `normalizePregnancyRecord` guarantees `fetuses` is
      // populated — a legacy record yields a single synthesised fetus, so a one-fetus pregnancy
      // renders exactly as it did before this change.
      const fetuses = Array.isArray(rec.fetuses) ? rec.fetuses : [];
      const fetusPids = [];
      const bornPids = [];

      fetuses.forEach((f, i) => {
        const fetus = f || {};
        // A born fetus is a real person now — it draws no fetus node, but a donor-conceived birth
        // keeps its donors pointing at that child (below).
        if (fetus.bornMemberId != null) {
          const born = findByMemberId(fetus.bornMemberId);
          if (born) bornPids.push(born.pid);
          return;
        }
        if (!fetusRendersAsNode(fetus)) return;

        const pid = nextPid++;
        fetusPids.push(pid);
        derived.push({
          pid,
          // The FIRST fetus keeps the record's own id as its uid, so pins / custom positions /
          // saved views captured before fetuses existed still resolve. Later fetuses get a
          // deterministic suffix (`:f1`, `:f2`…), which cannot collide with a donor uid
          // (`:0`, `:1`… — digits, never an `f` prefix).
          uid: i === 0 ? uid : `${uid}:f${i}`,
          gender:
            fetus.fetalSexKnown && fetus.gender ? fetus.gender : GENDER.unknown,
          isFetus: true,
          reproKind: REPRO_KIND.pregnancy,
          // Symbol text prints INSIDE the symbol (REQ-G7) — Catherine's use is numbering the
          // fetuses of a twin pregnancy.
          pedigreeSymbolText: fetus.pedigreeSymbolText,
          // Beside the symbol, behind the opt-in `fetusName` Reproductive label. Named
          // distinctly (not `name`) so it can never be mistaken for a person's name field.
          fetusName: fetus.name,
          // Fetus diagnoses reach the render as a member-shaped `diagnoses` array, so the
          // existing disease-shading pass shades an affected fetus with no special-casing
          // (clipped to the triangle/diamond by the symbol's own clip twin).
          ...(Array.isArray(fetus.diagnoses) && fetus.diagnoses.length
            ? { diagnoses: fetus.diagnoses }
            : {}),
          // Dysmorphic features (REQ-E1) reach the render the same way, as a member-shaped
          // `phenotypes` array — collectPhenotypeSource then finds them and the existing phenotype
          // label pass prints them beside the symbol.
          ...(Array.isArray(fetus.phenotypes) && fetus.phenotypes.length
            ? { phenotypes: fetus.phenotypes }
            : {}),
          pregnancy: {
            // Per-FETUS outcome drives the symbol shape; gestational age and multiplicity are
            // properties of the pregnancy and so are shared by its fetuses.
            outcome: fetus.outcome,
            gestationalWeeks: rec.gestationalWeeks,
            fetalSexKnown: !!fetus.fetalSexKnown,
            sexDeterminationMethod: fetus.sexDeterminationMethod,
            karyotype: fetus.karyotype,
            multiplicity: rec.multiplicity
          },
          motherId: carrierIsMother
            ? carrier.pid
            : partner
            ? partner.pid
            : null,
          fatherId: carrierIsMother
            ? partner
              ? partner.pid
              : null
            : carrier.pid,
          spouseIds: [],
          childrenIds: [],
          siblingIds: []
        });
        carrier.childrenIds.push(pid);
        if (partner) partner.childrenIds.push(pid);
      });

      // MULTIPLE GESTATION → draw them as twins (REQ-G3): slanted lines from a shared apex on
      // the sibship drop. Fetuses of one pregnancy already share both parent slots, so
      // createSiblingGroups puts them in one sibling group; these three fields are what promote
      // that group to a twin SUB-group (the same contract addSiblingsToRecords writes for people).
      //
      // ZYGOSITY follows Bennett (Tim's ruling: Bennett prevails over Desktop's single twin style).
      // The recorded zygosity drives the notation — a horizontal bar between monozygotic twins,
      // plain slanted lines for dizygotic, and a "?" when it is genuinely unknown. Unrecorded
      // zygosity therefore reads as "?" honestly, rather than asserting a zygosity we do not know.
      // TGCLOUD-1612 — the group spans the FETUSES **and** any co-fetus already converted to a
      // person. It used to be formed from `fetusPids` alone, so converting one twin of a pair
      // dropped the count to 1 and the twin notation vanished; meanwhile the person side only links
      // a child to co-fetuses that ALREADY carry a `bornMemberId`, so neither mechanism fired and
      // the relationship existed nowhere. A live-birth + stillbirth pair (Kathryn's repro) is stuck
      // there permanently, because a stillbirth cannot convert at all.
      //
      // `bornPids` is already collected above and already used for the donor lines below; the twin
      // group now uses that same union. Being DERIVED, this also repairs families already in the
      // broken state with no migration.
      const twinPids = [...fetusPids, ...bornPids];
      if (twinPids.length > 1) {
        const siblingType =
          SIBLING_TYPE_BY_COUNT[twinPids.length] || SIBLING_TYPE.quadruplet;
        const twinType =
          ZYGOSITY_TO_TWIN_TYPE[rec.zygosity] ?? TWIN_TYPE.unknown;
        const bornMembers = result.filter((m) => bornPids.includes(m.pid));
        // A group id already PERSISTED on a born child (linkTwinMembers writes one once a second
        // twin converts) wins, so this pass is a safety net rather than a rival owner of the group.
        const groupId =
          bornMembers.map((m) => m.twinSubGroupId).find(Boolean) ||
          `preg-twins-${uid}`;
        const relationsFor = (pid) =>
          twinPids
            .filter((p) => p !== pid)
            .map((withPid) => ({ withPid, type: twinType }));

        for (const node of derived) {
          if (!fetusPids.includes(node.pid)) continue;
          node.twinSubGroupId = groupId;
          node.siblingType = siblingType;
          node.twinRelations = relationsFor(node.pid);
          // Fetus-only, deliberately NOT widened to the born co-twin: the twin sub-group is formed
          // by grouping siblings on `twinSubGroupId` (Family.js), never from `siblingIds`, so
          // nothing needs it and widening it would touch a consumer we have not checked.
          node.siblingIds = fetusPids.filter((p) => p !== node.pid);
        }

        // A BORN co-twin is a REAL member, so only the twin fields may be written. These are
        // shallow copies made at the top of this function, so this stays render-only.
        for (const member of bornMembers) {
          member.twinSubGroupId = groupId;
          member.siblingType = siblingType;
          member.isTwin = siblingType === SIBLING_TYPE.twin;
          // MERGED, never replaced: a person can be twinned with someone OUTSIDE this pregnancy,
          // and overwriting the array would erase a hand-entered relation.
          const kept = (member.twinRelations || []).filter(
            (r) => !twinPids.some((p) => String(p) === String(r?.withPid))
          );
          member.twinRelations = [...kept, ...relationsFor(member.pid)];
          // `siblingIds` is deliberately UNTOUCHED. A real member has real sibling edges to
          // non-twin siblings; replacing them with "just the twins" would drop those siblings out
          // of the sibship and visibly change the chart.
        }
      }

      // Gamete donors — satellites carrying donorOfPids (never childrenIds / a mating line).
      // A donor contributed to the PREGNANCY, so it points at every fetus of it (and at any
      // already-born child): with more than one target in the same sibship the layout draws ONE
      // line to the sibship bar rather than a diagonal per fetus (Bennett 5.1c).
      const donorTargets = [...fetusPids, ...bornPids];
      const donors = Array.isArray(rec.donors) ? rec.donors : [];
      if (donorTargets.length) {
        donors.forEach((d, i) => {
          derived.push({
            pid: nextPid++,
            uid: `${uid}:${i}`,
            gender: DONOR_GENDER[d.role] ?? GENDER.unknown,
            reproKind: REPRO_KIND.gameteDonor,
            donorType: d.role,
            donorOfPids: donorTargets,
            spouseIds: [],
            childrenIds: [],
            siblingIds: []
          });
        });
      }
    }
  }

  return [...result, ...derived];
}
